import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  makeStyles,
  Button,
  Checkbox,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  ColorPicker as ColorPickerV9,
  ColorSlider,
  AlphaSlider,
  ColorArea,
  type ColorPickerProps,
  Dropdown,
  Option,
  mergeClasses,
  Field,
  FluentProvider,
  Input,
  InfoLabel,
  Label,
  Link,
  Spinner,
  webLightTheme,
} from '@fluentui/react-components';
import { OpenFilled } from '@fluentui/react-icons';
import { PersonAvailable24Regular } from '@fluentui/react-icons';
import { TinyColor } from '@ctrl/tinycolor';
import { useParams } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import { FontIcon, Panel, PanelType } from '@fluentui/react';
import {
  createAssetStatus,
  getAssetStatuses,
  IAssetStatus,
  updateAssetStatus,
} from '../../services/assetServices';
import {
  createWorkStatusGroup,
  deleteWorkStatusGroup,
  getWorkStatusGroups,
  type IWorkStatusGroup,
  type StatusGroupKind,
  type WorkStatusGroupAudience,
  updateWorkStatusGroup,
} from '../../services/workStatusGroupService';
import {
  createOrderStatus,
  createWorkStatus,
  getOrderStatuses,
  getWorkStatuses,
  IStatusPermission,
  IWorkStatus,
  updateOrderStatus,
  updateWorkStatus,
} from '../../services/workService';
import { getUserOrGroupsNames } from '../../services/userService';
import UserOrGroupSelector, {
  type IPermissionItem,
} from '../../common/selectors/UsersOrGroupSelectors';
import { EditItem } from '@moko/ui';

type LabelRole = 'client' | 'manager' | 'contractor';

interface IWorkStatusGroupItem {
  id: number;
  statusId?: number;
  audience: WorkStatusGroupAudience;
  label: string;
  code?: string;
  color?: string;
  icon?: string;
  dashboardCardNumber: number | null;
}

const AUDIENCE_BY_ROLE: Record<LabelRole, WorkStatusGroupAudience> = {
  client: 'Client',
  manager: 'Manager',
  contractor: 'Contractor',
};

const ROLE_TITLE: Record<LabelRole, string> = {
  client: 'Client status group labels',
  manager: 'Manager status group labels',
  contractor: 'Contractor status group labels',
};

const STATUS_LABEL_FIELD: Record<
  LabelRole,
  'clientLabel' | 'managerLabel' | 'contractorLabel'
> = {
  client: 'clientLabel',
  manager: 'managerLabel',
  contractor: 'contractorLabel',
};

const STATUS_GROUP_ID_FIELD: Record<
  LabelRole,
  'clientStatusGroupId' | 'managerStatusGroupId' | 'contractorStatusGroupId'
> = {
  client: 'clientStatusGroupId',
  manager: 'managerStatusGroupId',
  contractor: 'contractorStatusGroupId',
};

const roleSupportsDashboardCard = (role: LabelRole): boolean =>
  role === 'client' || role === 'manager';

const HEX_COLOR_REGEX = /^#?([0-9A-Fa-f]{0,8})$/;
const HEX_COMPLETE_REGEX =
  /^#?(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

const normalizeHex = (value: string): string | undefined => {
  const digits = value.trim().replace(/^#/, '');
  if (!HEX_COMPLETE_REGEX.test(digits)) return undefined;
  const tiny = new TinyColor(`#${digits}`);
  return tiny.isValid ? tiny.toHexString() : undefined;
};

const hexToHsv = (hex: string) => {
  const hsv = new TinyColor(hex).toHsv();
  return {
    h: hsv.h ?? 0,
    s: hsv.s ?? 0,
    v: hsv.v ?? 1,
    a: hsv.a ?? 1,
  };
};

const namedColorForHex = (hex: string, names: readonly string[]) => {
  const parsed = new TinyColor(hex);
  if (!parsed.isValid) return undefined;
  const target = parsed.toHexString().toLowerCase();
  return names.find(
    (name) => new TinyColor(name).toHexString().toLowerCase() === target
  );
};

type HsvColor = { h: number; s: number; v: number; a: number };

const HexColorInput = ({
  value,
  onValid,
  className,
}: {
  value: string;
  onValid: (hex: string) => void;
  className?: string;
}) => {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  return (
    <Input
      aria-label="Hex color"
      placeholder="#RRGGBB"
      className={className}
      value={draft}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        setDraft(value);
      }}
      onChange={(_, data) => {
        const next = data.value;
        if (!HEX_COLOR_REGEX.test(next)) return;
        setDraft(next);
        const hex = normalizeHex(next);
        if (hex) onValid(hex);
      }}
    />
  );
};

const HexColorControl = ({
  value,
  onValid,
  open,
  onOpenChange,
  children,
}: {
  value: string;
  onValid: (hex: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) => {
  const styles = useStyles();
  const swatchColor = new TinyColor(value).isValid
    ? new TinyColor(value).toRgbString()
    : '#ffffff';

  return (
    <div className={styles.hexGroup}>
      <HexColorInput
        value={value}
        onValid={onValid}
        className={styles.hexField}
      />
      <span className={styles.swatchWrap}>
        <Popover
          open={open}
          trapFocus
          onOpenChange={(_, d) => onOpenChange(d.open)}
        >
          <PopoverTrigger disableButtonEnhancement>
            <button
              type="button"
              className={styles.swatch}
              style={{ backgroundColor: swatchColor }}
              aria-label="Custom color picker"
            />
          </PopoverTrigger>
          <PopoverSurface>{children}</PopoverSurface>
        </Popover>
      </span>
    </div>
  );
};

const useStyles = makeStyles({
  picker: {
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  row: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
  },
  sliders: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    paddingLeft: '10px',
    paddingRight: '10px',
    boxSizing: 'border-box',
  },
  slider: {
    width: '100% !important',
    minWidth: '0px !important',
  },
  previewColor: {
    margin: '10px 0',
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    flexShrink: 0,
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
    },
  },
  alphaSlider: {
    width: '100%',
    minWidth: '0px',
    '& .fui-AlphaSlider__rail': {
      backgroundImage: `linear-gradient(to right, transparent, var(--fui-AlphaSlider__rail--color)), repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%)`,
      backgroundSize: 'auto, 8px 8px',
    },
  },
  hexGroup: {
    display: 'flex',
    alignItems: 'stretch',
    width: '160px',
  },
  hexField: {
    flex: 1,
    minWidth: 0,
    borderTopRightRadius: '0 !important',
    borderBottomRightRadius: '0 !important',
    '::after': {
      display: 'none !important',
    },
  },
  swatchWrap: {
    display: 'flex',
    flexShrink: 0,
  },
  swatch: {
    boxSizing: 'border-box',
    width: '32px',
    minWidth: '32px',
    height: '100%',
    margin: 0,
    padding: 0,
    border: '1px solid #ccc',
    borderLeft: 0,
    borderRadius: '0 4px 4px 0',
    cursor: 'pointer',
    appearance: 'none',
    boxShadow: 'none',
  },
  colorControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  panelColorControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    width: '100%',
  },
  field: {
    width: '100%',
    maxWidth: '500px',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
  },
  actionLabelInput: {
    flex: 1,
    minWidth: 0,
  },
  colorDropdown: {
    minWidth: '150px',
  },
  lookupLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  roleDropdown: {
    flex: 1,
    minWidth: 0,
  },
  roleField: {
    display: 'grid',
    gridTemplateColumns: '175px minmax(0, 1fr)',
    alignItems: 'center',
    gap: '0px',
    width: '100%',
    maxWidth: '500px',
  },
  roleFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    width: '100%',
  },
  roleFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '12px',
  },
  panelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  panelTopRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelRowFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelSecondRowWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelSecondRow: {
    display: 'grid',
    gridTemplateColumns: '1.8fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelHeaderActions: {
    display: 'flex',
    gap: '6px',
  },
  panelListHeader: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 92px',
    gap: '8px',
    padding: '6px 8px',
    borderBottom: '1px solid #edebe9',
    fontWeight: 600,
  },
  panelListHeaderWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 72px 92px',
    gap: '8px',
    padding: '6px 8px',
    borderBottom: '1px solid #edebe9',
    fontWeight: 600,
  },
  panelListRow: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 92px',
    gap: '8px',
    padding: '8px',
    borderBottom: '1px solid #f3f2f1',
    alignItems: 'center',
  },
  panelListRowWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 72px 92px',
    gap: '8px',
    padding: '8px',
    borderBottom: '1px solid #f3f2f1',
    alignItems: 'center',
  },
  panelListCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  panelActionsHeader: {
    textAlign: 'right',
  },
  panelCheckbox: {
    marginBottom: '6px',
  },
  panelActions: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'flex-end',
  },
  panelPreviewColor: {
    margin: '0',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
    },
  },
  panelListIcon: {
    width: '16px',
    height: '16px',
    fontSize: '16px',
    lineHeight: '16px',
  },
  panelActionIconButton: {
    minWidth: '32px',
    padding: '0',
  },
});

const ColorPickerFields = ({
  color,
  onColorChange,
  onHex,
  onOk,
  onCancel,
}: {
  color: HsvColor;
  onColorChange: ColorPickerProps['onColorChange'];
  onHex: (hex: string) => void;
  onOk: () => void;
  onCancel: () => void;
}) => {
  const styles = useStyles();
  return (
    <div className={styles.picker}>
      <ColorPickerV9 color={color} onColorChange={onColorChange}>
        <ColorArea
          inputX={{ 'aria-label': 'Saturation' }}
          inputY={{ 'aria-label': 'Brightness' }}
        />
        <div className={styles.row}>
          <div className={styles.sliders}>
            <ColorSlider aria-label="Hue" className={styles.slider} />
            <AlphaSlider
              aria-label="Alpha"
              className={mergeClasses(styles.slider, styles.alphaSlider)}
              style={{
                ['--fui-AlphaSlider__rail--color' as string]: new TinyColor(
                  color
                ).toHexString(),
                ['--fui-AlphaSlider--direction' as string]: 'to right',
              }}
            />
          </div>
          <div
            className={styles.previewColor}
            style={{
              backgroundColor: new TinyColor(color).toRgbString(),
            }}
          />
        </div>
      </ColorPickerV9>
      <HexColorInput
        value={new TinyColor(color).toHexString()}
        onValid={onHex}
      />
      <div className={styles.row}>
        <Button appearance="primary" onClick={onOk}>
          Ok
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const DEFAULT_COLOR_HSV = { h: 109, s: 1, v: 0.9, a: 1 };

const Status = () => {
  const isSaved = useRef(false);
  const { type } = useParams();

  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [icon, setIcon] = useState<string>();
  // Persisted color value for API (hex string like #RRGGBB)
  const [colorHex, setColorHex] = useState<string>('#ffffff');
  // Working color state for the popup (HSV as expected by v9 ColorPicker)
  const [previewColor, setPreviewColor] = useState(DEFAULT_COLOR_HSV);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [accesses, setAccesses] = useState<IPermissionItem[]>([]);
  const [allowReportDelivery, setAllowReportDelivery] = useState(false);
  const [deliverReportAutomatically, setDeliverReportAutomatically] =
    useState(false);
  const [useOnFormFinish, setUseOnFormFinish] = useState(false);
  const [showAsAction, setShowAsAction] = useState(false);
  const [actionLabel, setActionLabel] = useState('');
  const [statusClientLabel, setStatusClientLabel] = useState<string>();
  const [statusManagerLabel, setStatusManagerLabel] = useState<string>();
  const [statusContractorLabel, setStatusContractorLabel] = useState<string>();
  const [statusClientCode, setStatusClientCode] = useState<string>();
  const [statusManagerCode, setStatusManagerCode] = useState<string>();
  const [statusContractorCode, setStatusContractorCode] = useState<string>();
  const [statusClientGroupId, setStatusClientGroupId] = useState<number | null>(
    null
  );
  const [statusManagerGroupId, setStatusManagerGroupId] = useState<
    number | null
  >(null);
  const [statusContractorGroupId, setStatusContractorGroupId] = useState<
    number | null
  >(null);
  const [workStatusGroupItemsByRole, setWorkStatusGroupItemsByRole] = useState<
    Record<LabelRole, IWorkStatusGroupItem[]>
  >({
    client: [],
    manager: [],
    contractor: [],
  });
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);
  const [isLabelMutationInProgress, setIsLabelMutationInProgress] =
    useState(false);
  const [activeLabelPanelRole, setActiveLabelPanelRole] =
    useState<LabelRole | null>(null);
  const [newPanelLabel, setNewPanelLabel] = useState('');
  const [newPanelCode, setNewPanelCode] = useState('');
  const [newPanelColor, setNewPanelColor] = useState('');
  const [newPanelPreviewColor, setNewPanelPreviewColor] =
    useState(DEFAULT_COLOR_HSV);
  const [newPanelColorPopoverOpen, setNewPanelColorPopoverOpen] =
    useState(false);
  const [newPanelIcon, setNewPanelIcon] = useState('');
  const [newPanelDashboardCardNumber, setNewPanelDashboardCardNumber] =
    useState<string>('');
  const [activeStatusId, setActiveStatusId] = useState<number | null>(null);
  const [editingWorkStatusGroupId, setEditingWorkStatusGroupId] = useState<
    number | null
  >(null);
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null);
  const [
    isAccessSelectorOpen,
    { setTrue: showAccessSelector, setFalse: hideAccessSelector },
  ] = useBoolean(false);
  const styles = useStyles();

  // Work/Order statuses support per-status access permissions. Asset doesn't.
  const supportsPermissions = type === 'work' || type === 'order';
  const supportsContractorLabel = type === 'work';
  const statusGroupKind: StatusGroupKind = type === 'order' ? 'order' : 'work';

  const handleV9ColorChange: ColorPickerProps['onColorChange'] = (_, data) => {
    setPreviewColor({ ...data.color, a: data.color.a ?? 1 });
  };

  const cssNamedColors = [
    'AliceBlue',
    'AntiqueWhite',
    'Aqua',
    'Aquamarine',
    'Azure',
    'Beige',
    'Bisque',
    'Black',
    'BlanchedAlmond',
    'Blue',
    'BlueViolet',
    'Brown',
    'BurlyWood',
    'CadetBlue',
    'Chartreuse',
    'Chocolate',
    'Coral',
    'CornflowerBlue',
    'Cornsilk',
    'Crimson',
    'Cyan',
    'DarkBlue',
    'DarkCyan',
    'DarkGoldenRod',
    'DarkGray',
    'DarkGreen',
    'DarkKhaki',
    'DarkMagenta',
    'DarkOliveGreen',
    'DarkOrange',
    'DarkOrchid',
    'DarkRed',
    'DarkSalmon',
    'DarkSeaGreen',
    'DarkSlateBlue',
    'DarkSlateGray',
    'DarkTurquoise',
    'DarkViolet',
    'DeepPink',
    'DeepSkyBlue',
    'DimGray',
    'DodgerBlue',
    'FireBrick',
    'FloralWhite',
    'ForestGreen',
    'Fuchsia',
    'Gainsboro',
    'GhostWhite',
    'Gold',
    'GoldenRod',
    'Gray',
    'Green',
    'GreenYellow',
    'HoneyDew',
    'HotPink',
    'IndianRed',
    'Indigo',
    'Ivory',
    'Khaki',
    'Lavender',
    'LavenderBlush',
    'LawnGreen',
    'LemonChiffon',
    'LightBlue',
    'LightCoral',
    'LightCyan',
    'LightGoldenRodYellow',
    'LightGray',
    'LightGreen',
    'LightPink',
    'LightSalmon',
    'LightSeaGreen',
    'LightSkyBlue',
    'LightSlateGray',
    'LightSteelBlue',
    'LightYellow',
    'Lime',
    'LimeGreen',
    'Linen',
    'Magenta',
    'Maroon',
    'MediumAquaMarine',
    'MediumBlue',
    'MediumOrchid',
    'MediumPurple',
    'MediumSeaGreen',
    'MediumSlateBlue',
    'MediumSpringGreen',
    'MediumTurquoise',
    'MediumVioletRed',
    'MidnightBlue',
    'MintCream',
    'MistyRose',
    'Moccasin',
    'NavajoWhite',
    'Navy',
    'OldLace',
    'Olive',
    'OliveDrab',
    'Orange',
    'OrangeRed',
    'Orchid',
    'PaleGoldenRod',
    'PaleGreen',
    'PaleTurquoise',
    'PaleVioletRed',
    'PapayaWhip',
    'PeachPuff',
    'Peru',
    'Pink',
    'Plum',
    'PowderBlue',
    'Purple',
    'RebeccaPurple',
    'Red',
    'RosyBrown',
    'RoyalBlue',
    'SaddleBrown',
    'Salmon',
    'SandyBrown',
    'SeaGreen',
    'SeaShell',
    'Sienna',
    'Silver',
    'SkyBlue',
    'SlateBlue',
    'SlateGray',
    'Snow',
    'SpringGreen',
    'SteelBlue',
    'Tan',
    'Teal',
    'Thistle',
    'Tomato',
    'Turquoise',
    'Violet',
    'Wheat',
    'White',
    'WhiteSmoke',
    'Yellow',
    'YellowGreen',
  ];
  const selectedColorName = namedColorForHex(colorHex, cssNamedColors);
  const newPanelSelectedColorName = namedColorForHex(
    newPanelColor,
    cssNamedColors
  );

  const statusFields =
    type === 'work'
      ? [
          { name: 'name', fieldType: 'String', label: 'Name' },
          { name: 'code', fieldType: 'String', label: 'Code' },
          {
            name: 'isCompleted',
            fieldType: 'Checkbox',
            label: 'Is Completed',
          },
          {
            name: 'lockEntity',
            fieldType: 'Checkbox',
            label: 'Lock Entity',
          },
        ]
      : type === 'order'
      ? [
          { name: 'name', fieldType: 'String', label: 'Name' },
          { name: 'code', fieldType: 'String', label: 'Code' },
          {
            name: 'isCompleted',
            fieldType: 'Checkbox',
            label: 'Is Completed',
          },
          { name: 'lockEntity', fieldType: 'Checkbox', label: 'Lock Entity' },
        ]
      : [
          { name: 'name', fieldType: 'String', label: 'Name' },
          { name: 'code', fieldType: 'String', label: 'Code' },
          { name: 'lockEntity', fieldType: 'Checkbox', label: 'Lock Entity' },
        ];

  const toBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  };

  const toRequiredString = (value: unknown): string =>
    typeof value === 'string' ? value : '';

  const toOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
      return undefined;
    }
    return value;
  };

  const toHexOrEmpty = (value: string): string => {
    if (!value) return '';
    const tiny = new TinyColor(value);
    return tiny.isValid ? tiny.toHexString() : '';
  };

  const toDashboardCardNumber = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'boolean') {
      return value ? 1 : null;
    }
    return null;
  };

  const toLegacyDashboardFlag = (value: number | null): boolean =>
    value != null;

  const buildStatusGroupPayload = (
    role: LabelRole,
    label: string,
    statusGroupKind: StatusGroupKind,
    statusId: number
  ) => {
    const dashboardCardNumber = roleSupportsDashboardCard(role)
      ? toDashboardCardNumber(newPanelDashboardCardNumber)
      : null;

    const payload: {
      statusId: number;
      audience: WorkStatusGroupAudience;
      name: string;
      code?: string;
      color?: string;
      icon?: string;
      showInDashboardCard?: boolean | number | null;
      dashboardCardNumber?: number | null;
    } = {
      statusId,
      audience: AUDIENCE_BY_ROLE[role],
      name: label,
      code: toOptionalString(newPanelCode),
      color: toOptionalString(newPanelColor),
      icon: toOptionalString(newPanelIcon),
      showInDashboardCard: toLegacyDashboardFlag(dashboardCardNumber),
    };

    // Order status-group API currently rejects null/unknown numeric field.
    if (statusGroupKind === 'work' && dashboardCardNumber != null) {
      payload.dashboardCardNumber = dashboardCardNumber;
    }

    return payload;
  };

  const findGroupBySelection = (
    role: LabelRole,
    selection?: string
  ): IWorkStatusGroupItem | undefined => {
    if (!selection) return undefined;
    return workStatusGroupItemsByRole[role].find(
      (item) => item.label === selection || item.code === selection
    );
  };

  const resolveSelectionValue = (
    role: LabelRole,
    statusValue?: string
  ): { label?: string; code?: string } => {
    if (!statusValue) {
      return { label: undefined, code: undefined };
    }

    const item = workStatusGroupItemsByRole[role].find(
      (x) => x.code === statusValue || x.label === statusValue
    );

    if (!item) {
      return { label: statusValue, code: statusValue };
    }

    return {
      label: item.label,
      code: item.code ?? statusValue,
    };
  };

  const dropdownItemsFor = (
    role: LabelRole,
    selectedLabel?: string
  ): IWorkStatusGroupItem[] => {
    const items = workStatusGroupItemsByRole[role];
    if (!selectedLabel) return items;
    if (
      items.some(
        (item) => item.label === selectedLabel || item.code === selectedLabel
      )
    ) {
      return items;
    }
    return [
      ...items,
      {
        id: -1,
        statusId: undefined,
        audience: AUDIENCE_BY_ROLE[role],
        label: selectedLabel,
        code: undefined,
        color: undefined,
        icon: undefined,
        dashboardCardNumber: null,
      },
    ];
  };

  const resolveDropdownDisplayValue = (
    role: LabelRole,
    labelValue?: string,
    codeValue?: string
  ): string => {
    const byCode = codeValue
      ? findGroupBySelection(role, codeValue)
      : undefined;
    if (byCode?.label) return byCode.label;

    const byLabel = labelValue
      ? findGroupBySelection(role, labelValue)
      : undefined;
    if (byLabel?.label) return byLabel.label;

    return labelValue ?? codeValue ?? '';
  };

  const readStatusLabelValue = (
    status: IWorkStatus,
    role: LabelRole
  ): string | undefined => status[STATUS_LABEL_FIELD[role]];

  const readStatusGroupId = (
    status: IWorkStatus,
    role: LabelRole
  ): number | null => status[STATUS_GROUP_ID_FIELD[role]] ?? null;

  const loadRoleLabels = async (role: LabelRole) => {
    const abortController = new AbortController();
    const response = await getWorkStatusGroups(
      abortController,
      AUDIENCE_BY_ROLE[role],
      statusGroupKind
    );
    const nextItems: IWorkStatusGroupItem[] = (response ?? [])
      .filter((item: IWorkStatusGroup) => !item.isDeleted)
      .map((item: IWorkStatusGroup) => ({
        id: item.id,
        statusId: item.statusId,
        audience: item.audience,
        label: item.name,
        code: item.code,
        color: item.color,
        icon: item.icon,
        dashboardCardNumber: toDashboardCardNumber(
          item.dashboardCardNumber ?? item.showInDashboardCard
        ),
      }));

    setWorkStatusGroupItemsByRole((prev) => ({
      ...prev,
      [role]: nextItems,
    }));
  };

  const loadAllRoleLabels = async () => {
    if (!supportsPermissions) return;
    setIsLabelsLoading(true);
    try {
      const tasks: Promise<void>[] = [
        loadRoleLabels('client'),
        loadRoleLabels('manager'),
      ];
      if (supportsContractorLabel) {
        tasks.push(loadRoleLabels('contractor'));
      }
      await Promise.all(tasks);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to load work status groups.');
    } finally {
      setIsLabelsLoading(false);
    }
  };

  const resetPanelTemplate = () => {
    setNewPanelLabel('');
    setNewPanelCode('');
    setNewPanelColor('');
    setNewPanelPreviewColor(DEFAULT_COLOR_HSV);
    setNewPanelColorPopoverOpen(false);
    setNewPanelIcon('');
    setNewPanelDashboardCardNumber('');
    setEditingWorkStatusGroupId(null);
    setEditingStatusId(null);
  };

  const openLabelPanel = (role: LabelRole) => {
    if (activeStatusId == null) {
      setErrorMessage('Save the status before managing its labels.');
      return;
    }
    setActiveLabelPanelRole(role);
    resetPanelTemplate();
  };

  const closeLabelPanel = () => {
    setActiveLabelPanelRole(null);
    resetPanelTemplate();
  };

  const startTemplateEdit = (item: IWorkStatusGroupItem) => {
    setEditingWorkStatusGroupId(item.id);
    setEditingStatusId(item.statusId ?? null);
    setNewPanelLabel(item.label ?? '');
    setNewPanelCode(item.code ?? '');
    setNewPanelColor(new TinyColor(item.color || '#ffffff').toHexString());
    setNewPanelIcon(item.icon ?? '');
    setNewPanelDashboardCardNumber(
      item.dashboardCardNumber != null ? String(item.dashboardCardNumber) : ''
    );

    const hsv = new TinyColor(item.color || '#ffffff').toHsv();
    setNewPanelPreviewColor({
      h: hsv.h ?? 0,
      s: hsv.s ?? 0,
      v: hsv.v ?? 1,
      a: hsv.a ?? 1,
    });
  };

  const cancelTemplateEdit = () => {
    resetPanelTemplate();
  };

  const addLabelItem = async (role: LabelRole) => {
    const label = newPanelLabel.trim();
    if (!label) return;
    if (activeStatusId == null) {
      setErrorMessage('Save the status before managing its labels.');
      return;
    }
    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await createWorkStatusGroup(
        abortController,
        buildStatusGroupPayload(role, label, statusGroupKind, activeStatusId),
        statusGroupKind
      );
      setSuccessMessage('Label added.');
      setNewPanelLabel('');
      setNewPanelCode('');
      setNewPanelColor('');
      setNewPanelPreviewColor(DEFAULT_COLOR_HSV);
      setNewPanelColorPopoverOpen(false);
      setNewPanelIcon('');
      setNewPanelDashboardCardNumber('');
      await loadRoleLabels(role);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to create label.');
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const saveEditedWorkStatusGroup = async (role: LabelRole) => {
    if (editingWorkStatusGroupId == null) return false;

    const trimmedLabel = newPanelLabel.trim();
    if (!trimmedLabel) {
      setErrorMessage('Label value is required.');
      return false;
    }

    const statusId = editingStatusId ?? activeStatusId;
    if (statusId == null) {
      setErrorMessage('A parent status is required to update this label.');
      return false;
    }

    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await updateWorkStatusGroup(
        abortController,
        editingWorkStatusGroupId,
        {
          ...buildStatusGroupPayload(
            role,
            trimmedLabel,
            statusGroupKind,
            statusId
          ),
          isDeleted: false,
        },
        statusGroupKind
      );
      setSuccessMessage('Label updated.');
      await loadRoleLabels(role);
      resetPanelTemplate();
      return true;
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to update label.');
      return false;
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const removeLabelItem = async (role: LabelRole, itemId: number) => {
    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await deleteWorkStatusGroup(abortController, itemId, statusGroupKind);
      setSuccessMessage('Label deleted.');
      await loadRoleLabels(role);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to delete label.');
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const loadAccessesFromPermissions = async (
    permissions: IStatusPermission[],
    abortController: AbortController
  ) => {
    const items: IPermissionItem[] = permissions
      .map((p) => {
        if (p.userId != null) return { id: p.userId, isGroup: false };
        if (p.groupId != null) return { id: p.groupId, isGroup: true };
        return null;
      })
      .filter((x): x is IPermissionItem => x !== null);

    if (items.length === 0) {
      setAccesses([]);
      return;
    }

    const keyFor = (i: IPermissionItem) => `${i.isGroup ? 'G' : 'U'}${i.id}`;

    try {
      const ids = items.map(keyFor);
      const names: Record<string, string> | undefined =
        await getUserOrGroupsNames(abortController, ids);
      const lookup = new Map(Object.entries(names ?? {}));
      setAccesses(
        items.map((i) => {
          const resolvedName = lookup.get(keyFor(i));
          return {
            ...i,
            name: resolvedName,
            username: i.isGroup ? undefined : resolvedName,
          };
        })
      );
    } catch {
      setAccesses(items);
    }
  };

  /** Convert UI state back into the API permission shape. */
  const buildPermissionsPayload = (): IStatusPermission[] =>
    accesses.map((a) => ({
      groupId: a.isGroup ? a.id : null,
      userId: a.isGroup ? null : a.id,
      tenantId: null,
    }));

  useEffect(() => {
    if (!supportsPermissions) return;
    if (workStatusGroupItemsByRole.client.length > 0) return;
    void loadAllRoleLabels();
  }, [supportsPermissions]);

  return (
    <>
      <div>
        {type === 'work' ? 'Work' : type === 'order' ? 'Order' : 'Asset'} Status
      </div>
      <EditItem
        getAction={async (abortController, id) => {
          setActiveStatusId(id);
          const applyColorState = (status: IWorkStatus | IAssetStatus) => {
            setIcon(status.icon);
            const color = new TinyColor(
              status.color || '#ffffff'
            ).toHexString();
            setColorHex(color);
            const hsv = new TinyColor(color).toHsv();
            setPreviewColor({
              h: hsv.h ?? 0,
              s: hsv.s ?? 0,
              v: hsv.v ?? 1,
              a: hsv.a ?? 1,
            });
          };

          if (type === 'work' || type === 'order') {
            const data =
              type === 'work'
                ? await getWorkStatuses(abortController)
                : await getOrderStatuses(abortController);
            const status = data.find((s) => s.id === id);
            if (!status) return status;
            applyColorState(status);
            if (type === 'work') {
              setAllowReportDelivery(toBoolean(status.allowReportDelivery));
              setDeliverReportAutomatically(
                toBoolean(status.deliverReportAutomatically)
              );
              setUseOnFormFinish(toBoolean(status.useOnFormFinish));
              setShowAsAction(toBoolean(status.showAsAction));
              setActionLabel(status.actionLabel ?? '');
            }
            await loadAccessesFromPermissions(
              status.permissions ?? [],
              abortController
            );
            const clientSelection = resolveSelectionValue(
              'client',
              readStatusLabelValue(status, 'client')
            );
            const managerSelection = resolveSelectionValue(
              'manager',
              readStatusLabelValue(status, 'manager')
            );
            const contractorSelection = resolveSelectionValue(
              'contractor',
              readStatusLabelValue(status, 'contractor')
            );

            setStatusClientLabel(clientSelection.label);
            setStatusClientCode(clientSelection.code);
            setStatusClientGroupId(readStatusGroupId(status, 'client'));
            setStatusManagerLabel(managerSelection.label);
            setStatusManagerCode(managerSelection.code);
            setStatusManagerGroupId(readStatusGroupId(status, 'manager'));
            setStatusContractorLabel(
              supportsContractorLabel ? contractorSelection.label : undefined
            );
            setStatusContractorCode(
              supportsContractorLabel ? contractorSelection.code : undefined
            );
            setStatusContractorGroupId(
              supportsContractorLabel
                ? readStatusGroupId(status, 'contractor')
                : null
            );
            return {
              ...status,
              isCompleted: status.isCompleted ?? status.IsCompleted,
              lockEntity: status.lockEntity ?? status.LockEntity,
            };
          } else {
            const data: IAssetStatus[] = await getAssetStatuses(
              abortController
            );
            const status = data.find((s) => s.id === id);
            if (status) applyColorState(status);
            return status
              ? {
                  ...status,
                  lockEntity: status.lockEntity ?? status.LockEntity,
                }
              : status;
          }
        }}
        newAction={async (abortController, newStatus) => {
          if (type === 'asset') {
            const newStatusResponse = await createAssetStatus(abortController, {
              name: toRequiredString(newStatus.name),
              code: toOptionalString(newStatus.code),
              color: colorHex,
              icon: icon,
              lockEntity: toBoolean(newStatus.lockEntity),
            });
            setSuccessMessage(
              `Status with id: ${newStatusResponse.assetStatusId} created.`
            );
          } else {
            const createFn =
              type === 'order' ? createOrderStatus : createWorkStatus;
            const newStatusResponse = await createFn(abortController, {
              name: toRequiredString(newStatus.name),
              code: toOptionalString(newStatus.code),
              color: colorHex,
              icon: icon,
              isCompleted: toBoolean(newStatus.isCompleted),
              ...(type === 'work'
                ? {
                    allowReportDelivery,
                    deliverReportAutomatically:
                      allowReportDelivery && deliverReportAutomatically,
                    useOnFormFinish,
                    showAsAction,
                    actionLabel: showAsAction ? actionLabel : undefined,
                  }
                : {}),
              lockEntity: toBoolean(newStatus.lockEntity),
              clientStatusGroupId: statusClientGroupId,
              managerStatusGroupId: statusManagerGroupId,
              contractorStatusGroupId: supportsContractorLabel
                ? statusContractorGroupId
                : undefined,
              permissions: buildPermissionsPayload(),
            });
            setSuccessMessage(
              `Status with id: ${newStatusResponse.assetStatusId} created.`
            );
          }
        }}
        editAction={async (abortController, id, editedStatus) => {
          if (type === 'asset') {
            await updateAssetStatus(abortController, id, {
              name: toRequiredString(editedStatus.name),
              code: toOptionalString(editedStatus.code),
              color: colorHex,
              icon: icon,
              lockEntity: toBoolean(editedStatus.lockEntity),
            });
            setSuccessMessage(`Status with id: ${id} successfully updated.`);
          } else {
            const updateFn =
              type === 'order' ? updateOrderStatus : updateWorkStatus;
            await updateFn(abortController, id, {
              name: toRequiredString(editedStatus.name),
              code: toOptionalString(editedStatus.code),
              color: colorHex,
              icon: icon,
              isCompleted: toBoolean(editedStatus.isCompleted),
              ...(type === 'work'
                ? {
                    allowReportDelivery,
                    deliverReportAutomatically:
                      allowReportDelivery && deliverReportAutomatically,
                    useOnFormFinish,
                    showAsAction,
                    actionLabel: showAsAction ? actionLabel : undefined,
                  }
                : {}),
              lockEntity: toBoolean(editedStatus.lockEntity),
              clientStatusGroupId: statusClientGroupId,
              managerStatusGroupId: statusManagerGroupId,
              contractorStatusGroupId: supportsContractorLabel
                ? statusContractorGroupId
                : undefined,
              permissions: buildPermissionsPayload(),
            });
            setSuccessMessage(`Status with id: ${id} successfully updated.`);
          }
        }}
        isSaved={isSaved}
        back={`/settings/${type}Statuses`}
        preButtons={
          supportsPermissions
            ? [
                {
                  text: 'Access',
                  icon: <PersonAvailable24Regular />,
                  onClick: () => showAccessSelector(),
                },
              ]
            : undefined
        }
        metadata={{
          fields: statusFields,
          lookups: [],
          validations: [],
          interactions: [],
        }}
      >
        {type === 'work' && (
          <Field
            label="Allow Report Delivery"
            orientation="horizontal"
            className={styles.field}
          >
            <div className={styles.actionRow}>
              <Checkbox
                checked={allowReportDelivery}
                onChange={(_, data) =>
                  setAllowReportDelivery(data.checked === true)
                }
              />
              <Checkbox
                label="Deliver report automatically"
                labelPosition="before"
                checked={deliverReportAutomatically}
                disabled={!allowReportDelivery}
                onChange={(_, data) =>
                  setDeliverReportAutomatically(data.checked === true)
                }
              />
            </div>
          </Field>
        )}
        {type === 'work' && (
          <Field
            label="Show As Action"
            orientation="horizontal"
            className={styles.field}
          >
            <div className={styles.actionRow}>
              <Checkbox
                checked={showAsAction}
                onChange={(_, data) => setShowAsAction(data.checked === true)}
              />
              <Label htmlFor="status-action-label" disabled={!showAsAction}>
                Label
              </Label>
              <Input
                id="status-action-label"
                placeholder="Action label"
                className={styles.actionLabelInput}
                value={actionLabel}
                disabled={!showAsAction}
                onChange={(_, data) => setActionLabel(data.value)}
              />
            </div>
          </Field>
        )}
        {type === 'work' && (
          <Field
            label="Use on form finish"
            orientation="horizontal"
            className={styles.field}
          >
            <Checkbox
              label="Set this status when the contractor finishes the form"
              checked={useOnFormFinish}
              onChange={(_, data) => setUseOnFormFinish(data.checked === true)}
            />
          </Field>
        )}
        {supportsPermissions && (
          <div className={styles.roleFieldsContainer}>
            <Field
              label="Client Label"
              orientation="horizontal"
              className={styles.roleField}
            >
              <div className={styles.roleFieldRow}>
                <Dropdown
                  className={styles.roleDropdown}
                  placeholder="Select client label"
                  value={resolveDropdownDisplayValue(
                    'client',
                    statusClientLabel,
                    statusClientCode
                  )}
                  selectedOptions={
                    resolveDropdownDisplayValue(
                      'client',
                      statusClientLabel,
                      statusClientCode
                    )
                      ? [
                          resolveDropdownDisplayValue(
                            'client',
                            statusClientLabel,
                            statusClientCode
                          ),
                        ]
                      : []
                  }
                  onOptionSelect={(_e, data) => {
                    const selectedLabel = data.optionValue;
                    setStatusClientLabel(selectedLabel);
                    const selectedItem = findGroupBySelection(
                      'client',
                      selectedLabel
                    );
                    setStatusClientCode(selectedItem?.code ?? selectedLabel);
                    setStatusClientGroupId(selectedItem?.id ?? null);
                  }}
                >
                  {dropdownItemsFor('client', statusClientLabel).map((item) => (
                    <Option key={`${item.id}-${item.label}`} value={item.label}>
                      {item.label}
                    </Option>
                  ))}
                </Dropdown>
                <Link onClick={() => openLabelPanel('client')}>manage</Link>
              </div>
            </Field>

            <Field
              label="Manager Label"
              orientation="horizontal"
              className={styles.roleField}
            >
              <div className={styles.roleFieldRow}>
                <Dropdown
                  className={styles.roleDropdown}
                  placeholder="Select manager label"
                  value={resolveDropdownDisplayValue(
                    'manager',
                    statusManagerLabel,
                    statusManagerCode
                  )}
                  selectedOptions={
                    resolveDropdownDisplayValue(
                      'manager',
                      statusManagerLabel,
                      statusManagerCode
                    )
                      ? [
                          resolveDropdownDisplayValue(
                            'manager',
                            statusManagerLabel,
                            statusManagerCode
                          ),
                        ]
                      : []
                  }
                  onOptionSelect={(_e, data) => {
                    const selectedLabel = data.optionValue;
                    setStatusManagerLabel(selectedLabel);
                    const selectedItem = findGroupBySelection(
                      'manager',
                      selectedLabel
                    );
                    setStatusManagerCode(selectedItem?.code ?? selectedLabel);
                    setStatusManagerGroupId(selectedItem?.id ?? null);
                  }}
                >
                  {dropdownItemsFor('manager', statusManagerLabel).map(
                    (item) => (
                      <Option
                        key={`${item.id}-${item.label}`}
                        value={item.label}
                      >
                        {item.label}
                      </Option>
                    )
                  )}
                </Dropdown>
                <Link onClick={() => openLabelPanel('manager')}>manage</Link>
              </div>
            </Field>

            {supportsContractorLabel && (
              <Field
                label="Contractor Label"
                orientation="horizontal"
                className={styles.roleField}
              >
                <div className={styles.roleFieldRow}>
                  <Dropdown
                    className={styles.roleDropdown}
                    placeholder="Select contractor label"
                    value={resolveDropdownDisplayValue(
                      'contractor',
                      statusContractorLabel,
                      statusContractorCode
                    )}
                    selectedOptions={
                      resolveDropdownDisplayValue(
                        'contractor',
                        statusContractorLabel,
                        statusContractorCode
                      )
                        ? [
                            resolveDropdownDisplayValue(
                              'contractor',
                              statusContractorLabel,
                              statusContractorCode
                            ),
                          ]
                        : []
                    }
                    onOptionSelect={(_e, data) => {
                      const selectedLabel = data.optionValue;
                      setStatusContractorLabel(selectedLabel);
                      const selectedItem = findGroupBySelection(
                        'contractor',
                        selectedLabel
                      );
                      setStatusContractorCode(
                        selectedItem?.code ?? selectedLabel
                      );
                      setStatusContractorGroupId(selectedItem?.id ?? null);
                    }}
                  >
                    {dropdownItemsFor('contractor', statusContractorLabel).map(
                      (item) => (
                        <Option
                          key={`${item.id}-${item.label}`}
                          value={item.label}
                        >
                          {item.label}
                        </Option>
                      )
                    )}
                  </Dropdown>
                  <Link onClick={() => openLabelPanel('contractor')}>
                    manage
                  </Link>
                </div>
              </Field>
            )}
          </div>
        )}

        <Field
          label="Icon"
          orientation="horizontal"
          className={styles.field}
          hint={
            <div>
              <InfoLabel>Use an icon name from flicon.io</InfoLabel>
              <Link href="https://flicon.io" target="_blank">
                <OpenFilled />
              </Link>
            </div>
          }
        >
          <Input
            value={icon ?? ''}
            onChange={(_, newValue) => setIcon(newValue.value)}
          />
        </Field>
        <Field label="Color" orientation="horizontal" className={styles.field}>
          <div className={styles.colorControls}>
            <Dropdown
              placeholder="Select a color"
              value={selectedColorName ?? ''}
              selectedOptions={selectedColorName ? [selectedColorName] : []}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  const hex = new TinyColor(data.optionValue).toHexString();
                  setColorHex(hex);
                  setPreviewColor(hexToHsv(hex));
                }
              }}
              className={styles.colorDropdown}
            >
              {cssNamedColors.map((colorName) => (
                <Option key={colorName} value={colorName} text={colorName}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: colorName,
                        border: '1px solid #ccc',
                        borderRadius: '2px',
                      }}
                    />
                    {colorName}
                  </div>
                </Option>
              ))}
            </Dropdown>
            <span style={{ fontSize: '12px', color: '#666' }}>or</span>
            <HexColorControl
              value={colorHex}
              onValid={(hex) => {
                setColorHex(hex);
                setPreviewColor(hexToHsv(hex));
              }}
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <ColorPickerFields
                color={previewColor}
                onColorChange={handleV9ColorChange}
                onHex={(hex) => setPreviewColor(hexToHsv(hex))}
                onOk={() => {
                  setColorHex(new TinyColor(previewColor).toHexString());
                  setPopoverOpen(false);
                }}
                onCancel={() => setPopoverOpen(false)}
              />
            </HexColorControl>
          </div>
        </Field>
      </EditItem>
      {supportsPermissions && (
        <UserOrGroupSelector
          isOpen={isAccessSelectorOpen}
          hideUsersAndGroupsSelector={hideAccessSelector}
          permissions={accesses}
          onUserOrGroupSelected={(id, isGroup, username, name) => {
            if (accesses.some((a) => a.id === id && a.isGroup === isGroup)) {
              return;
            }
            // The DetailsList "User|Group Name" column reads `name`, so for
            // users we copy `username` into `name` to render the row right
            // after selection (same trick as Tenant.tsx, Bug 391).
            setAccesses([
              ...accesses,
              { id, isGroup, username, name: name ?? username },
            ]);
          }}
          onRemovePermission={(id) =>
            setAccesses(accesses.filter((a) => a.id !== id))
          }
        />
      )}

      <Panel
        isOpen={activeLabelPanelRole != null}
        onDismiss={closeLabelPanel}
        type={PanelType.medium}
        headerText={
          activeLabelPanelRole != null
            ? `Manage ${ROLE_TITLE[activeLabelPanelRole]}`
            : 'Manage labels'
        }
      >
        {activeLabelPanelRole != null && (
          <FluentProvider theme={webLightTheme}>
            <div className={styles.panelContainer}>
              <div className={styles.panelTopRow}>
                <Field label="New label">
                  <Input
                    value={newPanelLabel}
                    onChange={(_, data) => setNewPanelLabel(data.value)}
                  />
                </Field>
                <Field label="Code">
                  <Input
                    value={newPanelCode}
                    onChange={(_, data) => setNewPanelCode(data.value)}
                  />
                </Field>
              </div>

              <div
                className={
                  roleSupportsDashboardCard(activeLabelPanelRole)
                    ? styles.panelSecondRowWithDashboard
                    : styles.panelSecondRow
                }
              >
                <Field label="Color">
                  <div className={styles.panelColorControls}>
                    <Dropdown
                      placeholder="Select a color"
                      value={newPanelSelectedColorName ?? ''}
                      selectedOptions={
                        newPanelSelectedColorName
                          ? [newPanelSelectedColorName]
                          : []
                      }
                      onOptionSelect={(_, data) => {
                        if (!data.optionValue) return;
                        const hex = new TinyColor(
                          data.optionValue
                        ).toHexString();
                        setNewPanelColor(hex);
                        setNewPanelPreviewColor(hexToHsv(hex));
                      }}
                      className={styles.colorDropdown}
                    >
                      {cssNamedColors.map((colorName) => (
                        <Option
                          key={colorName}
                          value={colorName}
                          text={colorName}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: colorName,
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                              }}
                            />
                            {colorName}
                          </div>
                        </Option>
                      ))}
                    </Dropdown>
                    <HexColorControl
                      value={toHexOrEmpty(newPanelColor)}
                      onValid={(hex) => {
                        setNewPanelColor(hex);
                        setNewPanelPreviewColor(hexToHsv(hex));
                      }}
                      open={newPanelColorPopoverOpen}
                      onOpenChange={setNewPanelColorPopoverOpen}
                    >
                      <ColorPickerFields
                        color={newPanelPreviewColor}
                        onColorChange={(_, colorData) =>
                          setNewPanelPreviewColor({
                            ...colorData.color,
                            a: colorData.color.a ?? 1,
                          })
                        }
                        onHex={(hex) => setNewPanelPreviewColor(hexToHsv(hex))}
                        onOk={() => {
                          setNewPanelColor(
                            new TinyColor(newPanelPreviewColor).toHexString()
                          );
                          setNewPanelColorPopoverOpen(false);
                        }}
                        onCancel={() => setNewPanelColorPopoverOpen(false)}
                      />
                    </HexColorControl>
                  </div>
                </Field>
                <Field
                  label="Icon"
                  hint={
                    <div>
                      <InfoLabel>Use an icon name from flicon.io</InfoLabel>
                      <Link href="https://flicon.io" target="_blank">
                        <OpenFilled />
                      </Link>
                    </div>
                  }
                >
                  <Input
                    value={newPanelIcon}
                    onChange={(_, data) => setNewPanelIcon(data.value)}
                  />
                </Field>

                {roleSupportsDashboardCard(activeLabelPanelRole) && (
                  <Field label="Dashboard card">
                    <Input
                      type="number"
                      value={newPanelDashboardCardNumber}
                      onChange={(_, data) =>
                        setNewPanelDashboardCardNumber(data.value)
                      }
                      placeholder="Card number"
                    />
                  </Field>
                )}
              </div>

              <div className={styles.panelHeaderActions}>
                {editingWorkStatusGroupId == null ? (
                  <Button
                    appearance="primary"
                    disabled={
                      isLabelMutationInProgress ||
                      newPanelLabel.trim().length === 0
                    }
                    onClick={() => {
                      void addLabelItem(activeLabelPanelRole);
                    }}
                  >
                    Add
                  </Button>
                ) : (
                  <>
                    <Button
                      appearance="primary"
                      disabled={
                        isLabelMutationInProgress ||
                        newPanelLabel.trim().length === 0
                      }
                      onClick={async () => {
                        await saveEditedWorkStatusGroup(activeLabelPanelRole);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      appearance="secondary"
                      disabled={isLabelMutationInProgress}
                      onClick={() => {
                        cancelTemplateEdit();
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>

              {isLabelsLoading ? (
                <Spinner label="Loading labels..." />
              ) : (
                <>
                  <div
                    className={
                      roleSupportsDashboardCard(activeLabelPanelRole)
                        ? styles.panelListHeaderWithDashboard
                        : styles.panelListHeader
                    }
                  >
                    <span>Label</span>
                    <span>Code</span>
                    <span>Icon</span>
                    <span>Color</span>
                    {roleSupportsDashboardCard(activeLabelPanelRole) && (
                      <span>Dashboard</span>
                    )}
                    <span className={styles.panelActionsHeader}>Actions</span>
                  </div>

                  {workStatusGroupItemsByRole[activeLabelPanelRole].map(
                    (item) => (
                      <div
                        key={item.id}
                        className={
                          roleSupportsDashboardCard(activeLabelPanelRole)
                            ? styles.panelListRowWithDashboard
                            : styles.panelListRow
                        }
                      >
                        <span className={styles.panelListCell}>
                          {item.label}
                        </span>
                        <span className={styles.panelListCell}>
                          {item.code ?? ''}
                        </span>
                        <span className={styles.panelListCell}>
                          {item.icon ? (
                            <FontIcon
                              iconName={item.icon}
                              className={styles.panelListIcon}
                            />
                          ) : (
                            ''
                          )}
                        </span>
                        <div
                          className={styles.panelPreviewColor}
                          title={item.color ?? ''}
                          style={{
                            backgroundColor:
                              toHexOrEmpty(item.color ?? '') || '#ffffff',
                          }}
                        />
                        {roleSupportsDashboardCard(activeLabelPanelRole) && (
                          <span className={styles.panelListCell}>
                            {item.dashboardCardNumber != null
                              ? String(item.dashboardCardNumber)
                              : ''}
                          </span>
                        )}
                        <div className={styles.panelActions}>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<FontIcon iconName="Edit" />}
                            aria-label="Edit"
                            title="Edit"
                            className={styles.panelActionIconButton}
                            disabled={
                              isLabelMutationInProgress ||
                              editingWorkStatusGroupId != null
                            }
                            onClick={() => {
                              startTemplateEdit(item);
                            }}
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<FontIcon iconName="Delete" />}
                            aria-label="Delete"
                            title="Delete"
                            className={styles.panelActionIconButton}
                            disabled={
                              isLabelMutationInProgress ||
                              editingWorkStatusGroupId != null
                            }
                            onClick={() => {
                              void removeLabelItem(
                                activeLabelPanelRole,
                                item.id
                              );
                            }}
                          />
                        </div>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </FluentProvider>
        )}
      </Panel>
    </>
  );
};

export default Status;
